// pdfmake is loaded lazily on first use (see loadPdfMake below)
let pdfMakeInstance = null;

// ---------------------------------------------------------------------------
// Font loading helpers
// ---------------------------------------------------------------------------

/**
 * Detect the theme's dist/ folder URL from the enqueued script tag.
 */
function getDistUrl() {
  const script = document.querySelector('script[src*="dist/main.js"]');
  if (script) return script.src.replace(/main\.js.*$/, "");
  const link = document.querySelector('link[href*="dist/main.css"]');
  if (link) return link.href.replace(/main\.css.*$/, "");
  return "";
}

/**
 * Fetch a font file and return its contents as a base64 string.
 */
async function fetchFontAsBase64(url) {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Load DM Sans + Marcellus TTF fonts into a pdfmake virtual-file-system object.
 */
async function loadCustomFonts() {
  const fontBase = `${getDistUrl()}static/fonts/`;
  const names = [
    "DMSans-Regular.ttf",
    "DMSans-Medium.ttf",
    "Marcellus-Regular.ttf",
  ];

  const entries = await Promise.all(
    names.map(async (name) => [
      name,
      await fetchFontAsBase64(`${fontBase}${name}`),
    ])
  );

  const vfs = {};
  entries.forEach(([name, data]) => {
    vfs[name] = data;
  });
  return vfs;
}

/**
 * Dynamically import pdfmake, register custom fonts, configure font families.
 */
async function loadPdfMake() {
  if (pdfMakeInstance) return pdfMakeInstance;

  // Load only the pdfmake core – we supply our own fonts instead of Roboto
  const pdfMakeModule = await import(
    /* webpackChunkName: "pdfmake" */ "pdfmake/build/pdfmake"
  );
  const pdfMake = pdfMakeModule.default || pdfMakeModule;

  // Register custom font files
  const customVfs = await loadCustomFonts();
  if (typeof pdfMake.addVirtualFileSystem === "function") {
    pdfMake.addVirtualFileSystem(customVfs);
  } else {
    pdfMake.vfs = customVfs;
  }

  // Map font families → file names
  pdfMake.fonts = {
    DMSans: {
      normal: "DMSans-Regular.ttf",
      bold: "DMSans-Medium.ttf",
      italics: "DMSans-Regular.ttf",
      bolditalics: "DMSans-Medium.ttf",
    },
    Marcellus: {
      normal: "Marcellus-Regular.ttf",
      bold: "Marcellus-Regular.ttf",
      italics: "Marcellus-Regular.ttf",
      bolditalics: "Marcellus-Regular.ttf",
    },
  };

  pdfMakeInstance = pdfMake;
  return pdfMake;
}

// ---------------------------------------------------------------------------
// Logo & colours
// ---------------------------------------------------------------------------

const LOGO_SVG = [
  '<svg width="148" height="48" viewBox="0 0 148 48" xmlns="http://www.w3.org/2000/svg">',
  '<path d="M69.1534 29.0869H62.1727V16.4733H59.6592V31.4516H69.1534V29.0869Z" fill="black"/>',
  '<path d="M75.9413 16.4733H73.4277V31.4516H75.9413V16.4733Z" fill="black"/>',
  '<path d="M88.5035 31.4516L94.9045 16.4733H92.1344L87.4083 27.6743L82.6624 16.4733H79.9121L86.3131 31.4516H88.5035Z" fill="black"/>',
  '<path d="M101.392 16.4733H98.8789V31.4516H101.392V16.4733Z" fill="black"/>',
  '<path d="M119.777 23.9624C119.777 19.7224 116.318 16.4733 111.787 16.4733H107.105V31.4516H111.787C116.318 31.4516 119.777 28.2025 119.777 23.9624ZM109.619 18.7726H111.787C115.051 18.7726 117.221 21.0307 117.221 23.9624C117.221 26.8941 115.053 29.1523 111.787 29.1523H109.619V18.7726Z" fill="black"/>',
  '<path d="M126.091 27.5265H133.2V31.4516H135.714V22.3391C135.714 18.6054 133.244 16.2213 129.636 16.2213C126.027 16.2213 123.58 18.6054 123.58 22.3391V31.4516H126.094V27.5265H126.091ZM126.091 22.2955C126.091 19.9332 127.487 18.603 129.636 18.603C131.784 18.603 133.2 19.9332 133.2 22.2955V25.3338H126.091V22.2955Z" fill="black"/>',
  '<path d="M0 0V48H148V0H0ZM2.21507 45.8242V33.9835C4.65707 39.2073 8.954 43.4256 14.2697 45.8242H2.21507ZM23.3248 45.7952C11.5859 45.2258 2.21507 35.6675 2.21507 23.9988V2.17576H23.3248V45.7952ZM46.6521 45.8242H25.5695C26.1319 34.6547 35.2807 25.6682 46.6521 25.1157V45.8242ZM46.6521 22.9376C37.2763 23.3519 29.2596 28.9778 25.5423 36.9298V2.17576H46.6521V22.9376ZM145.785 45.8242H48.8671V2.17576H145.785V45.8218V45.8242Z" fill="black"/>',
  "</svg>",
].join("");

const COLORS = {
  black: "#000000",
  primary: "#269084",
  grey100: "#F2F3F0",
  grey200: "#dedede",
  white: "#FFFFFF",
};

// Timeline circle dimensions
const CIRCLE_R = 11;
const CIRCLE_D = CIRCLE_R * 2;

// ---------------------------------------------------------------------------
// Contact info helper
// ---------------------------------------------------------------------------

/**
 * Extract contact person details from the #modal-contact dialog in the DOM.
 * Returns null when no contact data is available.
 */
function getContactInfo() {
  const modal = document.getElementById("modal-contact");
  if (!modal) return null;

  const name = modal.querySelector(".font-heading.text-24");
  const label = modal.querySelector(".uppercase.text-14");
  const email = modal.querySelector('a[href^="mailto:"]');
  const phone = modal.querySelector('a[href^="tel:"]');

  if (!name && !email && !phone) return null;

  return {
    name: name ? name.textContent.trim() : "",
    label: label ? label.textContent.trim() : "",
    email: email ? email.textContent.trim() : "",
    phone: phone ? phone.textContent.trim() : "",
  };
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

/**
 * Initialize PDF generator – attaches click handler to all [data-generate-pdf] buttons.
 */
export default function initPdfGenerator() {
  const buttons = document.querySelectorAll("[data-generate-pdf]");
  if (!buttons.length) return;

  buttons.forEach((button) => {
    button.addEventListener("click", async () => {
      button.disabled = true;

      // Add a small spinning loader next to the button text
      const loader = document.createElement("span");
      loader.className = "pdf-loader";
      loader.style.cssText =
        "display:inline-block;width:14px;height:14px;margin-left:8px;border:2px solid currentColor;border-top-color:transparent;border-radius:50%;animation:pdf-spin .6s linear infinite;vertical-align:middle;";
      button.appendChild(loader);

      // Inject the keyframe animation if not already present
      if (!document.getElementById("pdf-loader-style")) {
        const style = document.createElement("style");
        style.id = "pdf-loader-style";
        style.textContent = "@keyframes pdf-spin{to{transform:rotate(360deg)}}";
        document.head.appendChild(style);
      }

      try {
        const pdfMake = await loadPdfMake();
        const content = buildContent();
        const contact = getContactInfo();

        const docDefinition = {
          pageSize: "A4",
          pageMargins: [40, 80, 40, 50],
          header: (currentPage) => {
            if (currentPage !== 1) return null;

            const logo = { svg: LOGO_SVG, width: 108, height: 35 };

            if (!contact) {
              return { ...logo, margin: [40, 20, 0, 0] };
            }

            const nameLine = [contact.name, contact.label]
              .filter(Boolean)
              .join("  ·  ");
            const detailLine = [contact.email, contact.phone]
              .filter(Boolean)
              .join("  |  ");

            return {
              columns: [
                { svg: LOGO_SVG, width: 108, height: 35, fit: [108, 35] },
                {
                  width: "*",
                  alignment: "right",
                  stack: [
                    ...(nameLine
                      ? [{ text: nameLine, fontSize: 9, bold: true, color: COLORS.black }]
                      : []),
                    ...(detailLine
                      ? [{ text: detailLine, fontSize: 8, color: "#666666", margin: [0, 2, 0, 0] }]
                      : []),
                  ],
                  margin: [0, nameLine && detailLine ? 6 : 10, 0, 0],
                },
              ],
              margin: [40, 20, 40, 0],
            };
          },
          footer: (currentPage, pageCount) => {
            const pageText = {
              text: `${currentPage} / ${pageCount}`,
              alignment: "right",
              fontSize: 8,
              color: "#999999",
            };

            if (!contact) {
              return { ...pageText, margin: [0, 15, 40, 0] };
            }

            const contactLine = [
              contact.name,
              contact.email,
              contact.phone,
            ]
              .filter(Boolean)
              .join("  |  ");

            return {
              columns: [
                {
                  width: "*",
                  text: contactLine,
                  fontSize: 8,
                  color: "#666666",
                },
                { width: "auto", ...pageText },
              ],
              margin: [40, 15, 40, 0],
            };
          },
          content,
          styles: getStyles(),
          defaultStyle: {
            font: "DMSans",
            fontSize: 10,
            lineHeight: 1.25,
            color: COLORS.black,
          },
        };

        const fileName = "livida-pakkumine.pdf";

        pdfMake.createPdf(docDefinition).download(fileName);
        loader.remove();
        button.disabled = false;
      } catch (error) {
        console.error("PDF generation failed:", error);
        loader.remove();
        button.disabled = false;
      }
    });
  });
}

// ---------------------------------------------------------------------------
// Content builders
// ---------------------------------------------------------------------------

/**
 * Assemble the full PDF content array by reading the page DOM.
 */
function buildContent() {
  const content = [];

  // 1. Offer title
  const titleEl = document.querySelector("article h1");
  if (titleEl) {
    content.push({
      text: titleEl.textContent.trim(),
      style: "title",
      margin: [0, 0, 0, 16],
    });
  }

  // 2. Offer intro (the first .body-text with .mt-24 — unique to the intro)
  const introEl = document.querySelector("article .mt-24.body-text");
  if (introEl) {
    content.push(...parseHtmlContent(introEl));
  }

  // 3. Offer detail sections (grey boxes)
  content.push(...buildDetailSections());

  // 4. Free-form text content block
  content.push(...buildTextContent());

  // 5. Timeline – keep together on one page when possible;
  //    if it doesn't fit on the current page, pdfmake moves it to the next.
  const timeline = buildTimeline();
  if (timeline.length > 0) {
    content.push({
      stack: timeline,
      unbreakable: true,
    });
  }

  return content;
}

/**
 * Build the offer-detail sections (grey boxes with 3-col grids + pill buttons).
 */
function buildDetailSections() {
  const content = [];

  const greyBoxes = document.querySelectorAll(
    "article .rounded-16.bg-grey-100"
  );

  greyBoxes.forEach((greyBox) => {
    const parentLi = greyBox.closest("li");
    if (!parentLi) return;

    // --- Section title ---
    const sectionTitleEl = parentLi.querySelector(".text-20");
    if (sectionTitleEl) {
      content.push({
        text: sectionTitleEl.textContent.trim(),
        style: "sectionLabel",
        margin: [0, 24, 0, 10],
      });
    }

    // --- Collect detail items ---
    const detailLis = greyBox.querySelectorAll(":scope > ul > li");
    const items = [];
    detailLis.forEach((li) => {
      const label = li.querySelector("label");
      const value = li.querySelector(".font-heading");
      if (label || value) {
        items.push({
          label: label ? label.textContent.trim() : "",
          value: value ? value.textContent.trim() : "",
        });
      }
    });

    // --- Collect buttons / links ---
    const buttonEls = greyBox.querySelectorAll("a");
    const buttons = [];
    buttonEls.forEach((btn) => {
      const href = btn.getAttribute("href");
      const text = btn.textContent.trim();
      if (href && text) {
        buttons.push({ text, href });
      }
    });

    // --- Build table rows: 3 detail items per row ---
    const tableRows = [];

    for (let i = 0; i < items.length; i += 3) {
      const row = [];
      for (let j = 0; j < 3; j++) {
        const item = items[i + j];
        if (item) {
          row.push({
            stack: [
              { text: item.label.toUpperCase(), style: "detailLabel" },
              {
                text: item.value,
                style: "detailValue",
                margin: [0, 3, 0, 0],
              },
            ],
          });
        } else {
          row.push({ text: "" });
        }
      }
      tableRows.push(row);
    }

    // --- Add underlined text-link row that spans all 3 columns ---
    if (buttons.length > 0) {
      const linkTexts = buttons.map((btn) => ({
        text: btn.text,
        link: btn.href,
        fontSize: 9,
        bold: true,
        color: COLORS.black,
        decoration: "underline",
        margin: [0, 0, 14, 0],
      }));

      tableRows.push([
        {
          colSpan: 3,
          columns: [
            ...linkTexts.map((l) => ({ ...l, width: "auto" })),
            { text: "", width: "*" },
          ],
          columnGap: 0,
          margin: [0, 6, 0, 0],
        },
        {},
        {},
      ]);
    }

    // --- Render the grey-background table ---
    if (tableRows.length > 0) {
      content.push({
        table: {
          widths: ["*", "*", "*"],
          body: tableRows,
        },
        layout: {
          fillColor: () => COLORS.grey100,
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 16,
          paddingRight: () => 16,
          paddingTop: (i) => (i === 0 ? 16 : 10),
          paddingBottom: (i, node) =>
            i === node.table.body.length - 1 ? 16 : 4,
        },
      });
    }
  });

  return content;
}

/**
 * Build the free-text content section (title + body-text).
 */
function buildTextContent() {
  const content = [];

  const flexContainer = document.querySelector(
    "article .content .flex.flex-wrap.w-full"
  );
  if (!flexContainer) return content;

  const leftColumn = flexContainer.children[0];
  if (!leftColumn) return content;

  const textContentDiv = leftColumn.querySelector(":scope > .mt-48");
  if (!textContentDiv) return content;

  const titleEl = textContentDiv.querySelector("h2");
  if (titleEl) {
    content.push({
      text: titleEl.textContent.trim(),
      style: "sectionTitle",
      margin: [0, 32, 0, 16],
    });
  }

  const bodyText = textContentDiv.querySelector(".body-text");
  if (bodyText) {
    content.push(...parseHtmlContent(bodyText));
  }

  return content;
}

/**
 * Build the timeline section (steps with rounded circle indicators).
 */
function buildTimeline() {
  const content = [];

  const timelineList = document.querySelector(".timeline-list");
  if (!timelineList) return content;

  // Timeline title
  const prevSibling = timelineList.previousElementSibling;
  if (prevSibling && prevSibling.tagName === "SPAN") {
    content.push({
      text: prevSibling.textContent.trim(),
      style: "sectionLabel",
      margin: [0, 32, 0, 12],
    });
  }

  const steps = timelineList.querySelectorAll(":scope > li");
  if (!steps.length) return content;

  const tableBody = [];

  steps.forEach((step, index) => {
    const circleEl = step.querySelector(".rounded-full");
    const isCompleted = circleEl && circleEl.classList.contains("bg-black");

    const stepTitleEl = step.querySelector(".text-16");
    const stepTitle = stepTitleEl ? stepTitleEl.textContent.trim() : "";

    const tags = step.querySelectorAll(".tag");
    const tagTexts = Array.from(tags)
      .map((t) => t.textContent.replace(/\s+/g, " ").trim())
      .filter(Boolean);

    // --- Rounded circle indicator (canvas circle + checkmark or number) ---
    let indicator;

    if (isCompleted) {
      // Filled black circle with a small canvas-drawn checkmark
      indicator = {
        canvas: [
          {
            type: "ellipse",
            x: CIRCLE_R,
            y: CIRCLE_R,
            r1: CIRCLE_R,
            r2: CIRCLE_R,
            lineWidth: 0.75,
            lineColor: COLORS.black,
            color: COLORS.black,
          },
          // Small checkmark: two white line segments
          {
            type: "line",
            x1: 7,
            y1: CIRCLE_R + 0.5,
            x2: 10,
            y2: CIRCLE_R + 3,
            lineWidth: 1.2,
            lineColor: COLORS.white,
            lineCap: "round",
          },
          {
            type: "line",
            x1: 10,
            y1: CIRCLE_R + 3,
            x2: 15,
            y2: CIRCLE_R - 3,
            lineWidth: 1.2,
            lineColor: COLORS.white,
            lineCap: "round",
          },
        ],
        width: CIRCLE_D,
        height: CIRCLE_D,
      };
    } else {
      // White circle with step number overlaid (number nudged slightly up)
      indicator = {
        stack: [
          {
            canvas: [
              {
                type: "ellipse",
                x: CIRCLE_R,
                y: CIRCLE_R,
                r1: CIRCLE_R,
                r2: CIRCLE_R,
                lineWidth: 0.75,
                lineColor: COLORS.black,
                color: COLORS.white,
              },
            ],
            width: CIRCLE_D,
            height: CIRCLE_D,
          },
          {
            text: String(index + 1),
            font: "DMSans",
            fontSize: 9,
            bold: true,
            color: COLORS.black,
            alignment: "center",
            width: CIRCLE_D,
            relativePosition: { x: -1.6, y: -(CIRCLE_R + 5.5) },
          },
        ],
        width: CIRCLE_D,
        unbreakable: true,
      };
    }

    // --- Step content ---
    const stepStack = [{ text: stepTitle, fontSize: 10, lineHeight: 1.15 }];

    tagTexts.forEach((tag) => {
      stepStack.push({
        text: tag,
        fontSize: 8,
        color: "#666666",
        margin: [0, 2, 0, 0],
      });
    });

    tableBody.push([
      { stack: [indicator], margin: [0, 5, 0, 0] },
      { stack: stepStack, margin: [6, 3, 0, 0] },
    ]);
  });

  if (tableBody.length > 0) {
    content.push({
      table: {
        widths: [CIRCLE_D + 4, "*"],
        body: tableBody,
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 4,
        paddingBottom: () => 4,
      },
    });
  }

  return content;
}

// ---------------------------------------------------------------------------
// HTML → pdfmake parser
// ---------------------------------------------------------------------------

/**
 * Parse an HTML element's child nodes into an array of pdfmake content objects.
 */
function parseHtmlContent(element) {
  const result = [];

  Array.from(element.childNodes).forEach((node) => {
    const parsed = parseNode(node);
    if (parsed !== null) {
      if (Array.isArray(parsed)) {
        result.push(...parsed.filter((p) => p !== null));
      } else {
        result.push(parsed);
      }
    }
  });

  return result;
}

/**
 * Recursively convert a single DOM node into a pdfmake content node.
 */
function parseNode(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent;
    if (!text.trim()) return null;
    return { text };
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const tag = node.tagName.toLowerCase();

  const children = [];
  Array.from(node.childNodes).forEach((child) => {
    const parsed = parseNode(child);
    if (parsed !== null) {
      if (Array.isArray(parsed)) {
        children.push(...parsed.filter((p) => p !== null));
      } else {
        children.push(parsed);
      }
    }
  });

  switch (tag) {
    case "p":
      return {
        text: children.length ? children : [{ text: " " }],
        margin: [0, 0, 0, 8],
        lineHeight: 1.4,
      };

    case "h2":
      return {
        text: children.length ? children : [{ text: "" }],
        style: "sectionTitle",
        margin: [0, 16, 0, 8],
      };

    case "h3":
      return {
        text: children.length ? children : [{ text: "" }],
        style: "subTitle",
        margin: [0, 12, 0, 8],
      };

    case "strong":
    case "b":
      return children.length === 1
        ? { ...children[0], bold: true }
        : { text: children, bold: true };

    case "em":
    case "i":
      return children.length === 1
        ? { ...children[0], italics: true }
        : { text: children, italics: true };

    case "a": {
      const href = node.getAttribute("href");
      const result = {
        text: children.length ? children : [{ text: node.textContent }],
        color: COLORS.primary,
        decoration: "underline",
      };
      if (href) result.link = href;
      return result;
    }

    case "ul": {
      const items = children
        .filter((c) => c !== null)
        .map((c) => (c.text !== undefined ? c : { text: [c] }));
      return items.length ? { ul: items, margin: [0, 4, 0, 8] } : null;
    }

    case "ol": {
      const items = children
        .filter((c) => c !== null)
        .map((c) => (c.text !== undefined ? c : { text: [c] }));
      return items.length ? { ol: items, margin: [0, 4, 0, 8] } : null;
    }

    case "li":
      if (children.length === 0) return null;
      if (children.length === 1) return children[0];
      return { text: children };

    case "br":
      return { text: "\n" };

    default:
      if (children.length === 0) return null;
      if (children.length === 1) return children[0];
      return { text: children };
  }
}

// ---------------------------------------------------------------------------
// Styles & helpers
// ---------------------------------------------------------------------------

function getStyles() {
  return {
    title: {
      font: "Marcellus",
      fontSize: 22,
      lineHeight: 1.0,
    },
    sectionTitle: {
      font: "Marcellus",
      fontSize: 15,
      lineHeight: 1.1,
    },
    sectionLabel: {
      fontSize: 13,
      lineHeight: 1.0,
    },
    subTitle: {
      font: "Marcellus",
      fontSize: 12,
      lineHeight: 1.1,
    },
    detailLabel: {
      fontSize: 8,
      bold: true,
      color: COLORS.black,
    },
    detailValue: {
      font: "Marcellus",
      fontSize: 15,
      color: COLORS.black,
      lineHeight: 1.05,
    },
  };
}

/**
 * Convert a string to a URL-safe slug (handles Estonian characters).
 */
function slugify(text) {
  const charMap = {
    ä: "a",
    ö: "o",
    ü: "u",
    õ: "o",
    Ä: "a",
    Ö: "o",
    Ü: "u",
    Õ: "o",
  };

  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[äöüõÄÖÜÕ]/g, (c) => charMap[c] || c)
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+|-+$/g, "");
}
